import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Save, Plus, Trash2, AlertTriangle, CheckCircle2,
  Gauge, Anchor, Waves, ShieldAlert, ListChecks, Building2, Library,
} from "lucide-react";
import { Field, TextInput, TextArea, Checkbox } from "@/components/ReportPrimitives";
import FloatingActionToolbar from "@/components/FloatingActionToolbar";
import {
  emptyRigSheet, emptyPump, validateRigSheet, rigSheetSoftNotices,
  RIG_TYPES, PUMP_TYPES_V2, PUMP_TYPE_LABELS_V2, SUBSEA_EMPHASISED_TYPES,
} from "@/lib/rigsheet/schema";
import { computePumpDerived, recomputeAllPumps } from "@/lib/rigsheet/compute";
import { createRigSheet, updateRigSheet, getRigSheet } from "@/lib/rigsheet/storage";
import RceEditorStrip from "@/components/rce/RceEditorStrip";
import { listWellProfiles } from "@/lib/well/storage";
import { useStackMode } from "@/lib/stackModeContext";
import { getEngineeringSession, updateEngineeringSession } from "@/lib/rce/session";
import { listByCategory, findById } from "@/lib/reflibs/lookup";
import {
  libraryReturnState, getRestoredDraft, getRestoredAnchor, scrollToRestoredAnchor,
} from "@/lib/reflibs/returnContext";
import { toast } from "sonner";

// Subsea Foundation Correction Pass — Rig Type is grouped from the existing
// 4-value RIG_TYPES enum only (no new vocabulary). SUBSEA = floater/
// subsea-BOP-compatible types; SURFACE = every remaining surface-BOP type
// already in the enum (both onshore and offshore surface rigs).
const SUBSEA_RIG_TYPES  = ["Semi", "Drillship"];
const SURFACE_RIG_TYPES = RIG_TYPES.filter((t) => !SUBSEA_RIG_TYPES.includes(t));

// ---- Shared compact primitives (rig-sheet-local, not under /pages/profiles)
const SECTION_CARDS = [
  { id: "identity",   title: "Rig Identity",                Icon: Building2 },
  { id: "pumps",      title: "Mud Pumps",                   Icon: Gauge },
  { id: "surfline",   title: "Surface Line Data",           Icon: ListChecks },
  { id: "volumes",    title: "Well Control Volumes",        Icon: Waves },
  { id: "surfbop",    title: "Surface BOP Stack",           Icon: ShieldAlert },
  { id: "subsea",     title: "Subsea / Marine System",      Icon: Anchor },
  { id: "choke",      title: "Choke Manifold",              Icon: ListChecks },
  { id: "defaults",   title: "Rig Defaults & Notes",        Icon: ListChecks },
];

const u = {
  vol:     (units) => (units === "SI" ? "m³"     : "bbl"),
  depth:   (units) => (units === "SI" ? "m"      : "ft"),
  dia:     (units) => (units === "SI" ? "mm"     : "in"),
  length:  (units) => (units === "SI" ? "m"      : "ft"),
  liner:   (units) => (units === "SI" ? "mm"     : "in"),
  stroke:  (units) => (units === "SI" ? "mm"     : "in"),
  press:   (units) => (units === "SI" ? "kPa"    : "psi"),
};

// Carries the invalid set to the in-file field wrappers below without
// threading a prop through all eight section components. `clear` removes one
// field's marker the moment that field is edited — a red field the operator
// has already corrected is misleading, so the marker is per-field and
// disappears on that field's first keystroke.
const InvalidFieldsContext = React.createContext(null);

function NumIn({ value, onChange, placeholder, testId, disabled }) {
  const inv = React.useContext(InvalidFieldsContext);
  return (
    <TextInput type="number" value={value ?? ""}
               onChange={(v) => { if (testId) inv?.clear(testId); onChange(v); }}
               placeholder={placeholder} testId={testId} disabled={disabled}
               invalid={!!testId && !!inv?.has(testId)} />
  );
}

function NativeSelect({ value, onChange, options, testId }) {
  const inv = React.useContext(InvalidFieldsContext);
  const invalid = !!testId && !!inv?.has(testId);
  return (
    <select
      value={value ?? ""}
      onChange={(e) => { if (testId) inv?.clear(testId); onChange(e.target.value); }}
      data-testid={testId}
      aria-invalid={invalid ? true : undefined}
      style={invalid ? { borderColor: "#FCA5A5", background: "rgba(252,165,165,0.06)" } : undefined}
      className="input-rs">
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function SectionCard({ id, title, Icon, emphasised, children }) {
  return (
    <section id={`rs-section-${id}`}
             data-testid={`rs-section-${id}`}
             className="card-rs p-4"
             style={emphasised ? { borderColor: "var(--rs-amber)" } : undefined}>
      <header className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: "var(--rs-amber)" }} />
        <h2 className="text-[13px] font-semibold tracking-wide text-[var(--rs-text-1)]">{title}</h2>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

// =============================================================================
// Section components — defined locally so the editor remains a single coherent
// file. Each accepts (payload, set) where `set(patch)` merges a partial.
// =============================================================================

function IdentitySection({ payload, set, stackMode }) {
  const inv = React.useContext(InvalidFieldsContext);
  // Module 3.2a bridge — load well list once for the picker.
  const wells = React.useMemo(() => listWellProfiles(), []);
  const onPickWell = (wellId) => {
    if (!wellId) {
      set({ linkedWellId: "" });
      return;
    }
    const w = wells.find((x) => x.id === wellId);
    if (!w) { set({ linkedWellId: wellId }); return; }
    // Auto-fill identity from the well — user can still override afterwards.
    set({
      linkedWellId: wellId,
      rigName:      w.rigName || payload.rigName,
      rigContractor:w.rigContractor || payload.rigContractor,
      operator:     w.operator || payload.operator,
      field:        w.field || payload.field,
      block:        w.block || payload.block,
      fieldOrBlock: w.field || payload.fieldOrBlock,
      country:      w.country || payload.country,
    });
  };
  // Subsea Foundation Correction Pass — restrict Rig Type to the subset
  // compatible with the current global operating mode. An existing
  // mismatched value (set before this correction, or before a global mode
  // flip) is never silently replaced — it's injected into the options list
  // with a "(context mismatch)" marker and flagged below instead.
  const contextTypes = stackMode === "subsea" ? SUBSEA_RIG_TYPES : SURFACE_RIG_TYPES;
  const rigTypeMismatch = !!payload.rigType && !contextTypes.includes(payload.rigType);
  const rigTypeOptions = contextTypes.map((x) => ({ value: x, label: x }));
  if (rigTypeMismatch) rigTypeOptions.push({ value: payload.rigType, label: `${payload.rigType} (context mismatch)` });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Field label="Linked Well"
             helper="The Well this rig is drilling. Enables automatic engineering inheritance across Kill Sheets, BHA, Drill String and reports.">
        <NativeSelect value={payload.linkedWellId || ""}
                      onChange={onPickWell}
                      options={[
                        { value: "", label: "— No linked well —" },
                        ...wells.map((w) => ({ value: w.id, label: `${w.wellName || w.id}${w.field ? " · " + w.field : ""}` })),
                      ]}
                      testId="rs-linked-well" />
      </Field>
      <Field label="Rig Name (required)">
        <TextInput value={payload.rigName}
                   onChange={(v) => { inv?.clear("rs-rig-name"); set({ rigName: v }); }}
                   placeholder="e.g. Discoverer Spirit" testId="rs-rig-name"
                   invalid={!!inv?.has("rs-rig-name")} />
      </Field>
      <Field label="Rig Type (required)"
             helper={`${stackMode === "subsea" ? "Subsea" : "Surface"} operating mode — showing compatible rig types only.`}>
        <NativeSelect value={payload.rigType} onChange={(v) => set({ rigType: v })}
                      options={rigTypeOptions}
                      testId="rs-rig-type" />
        {rigTypeMismatch && (
          <p className="text-[11px] text-amber-400 mt-1" data-testid="rs-rig-type-mismatch">
            This rig type doesn't match the current operating context (Surface/Subsea) — review before proceeding.
          </p>
        )}
      </Field>
      <Field label="Default Unit System (required)"
             helper="Imperial primary. Switch to Metric before entering metric values.">
        <div className="inline-flex rounded-md overflow-hidden"
             style={{ border: "1px solid var(--rs-border)" }}>
          {["IMP", "SI"].map((u) => (
            <button key={u} type="button" data-testid={`rs-units-${u.toLowerCase()}`}
                    onClick={() => set({ units: u })}
                    className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider"
                    style={{
                      background: payload.units === u ? "var(--rs-amber)" : "transparent",
                      color: payload.units === u ? "#0A0B0E" : "var(--rs-text-2)",
                      minHeight: 32, minWidth: 64,
                    }}>
              {u === "IMP" ? "Imperial" : "Metric"}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Operator">
        <TextInput value={payload.operator} onChange={(v) => set({ operator: v })}
                   placeholder="e.g. Equinor" testId="rs-operator" />
      </Field>
      <Field label="Rig Contractor">
        <TextInput value={payload.rigContractor} onChange={(v) => set({ rigContractor: v })}
                   placeholder="e.g. Transocean" testId="rs-contractor" />
      </Field>
      <Field label="Field"
             helper="Inherited by the Well Profile as its Field.">
        <TextInput value={payload.field} onChange={(v) => set({ field: v })}
                   placeholder="e.g. Mad Dog" testId="rs-field" />
      </Field>
      <Field label="Block"
             helper="Inherited by the Well Profile as its Block.">
        <TextInput value={payload.block} onChange={(v) => set({ block: v })}
                   placeholder="e.g. GC 781" testId="rs-block" />
      </Field>
      {/* Legacy combined value — only shown for sheets saved before Field and
          Block became separate, so that data stays visible and editable
          instead of silently disappearing. New sheets never see this. */}
      {payload.fieldOrBlock && (
        <Field label="Field / Block / Area (legacy)"
               helper="Superseded by the separate Field and Block above. Copy the values across, then clear this.">
          <TextInput value={payload.fieldOrBlock} onChange={(v) => set({ fieldOrBlock: v })}
                     testId="rs-field-legacy" />
        </Field>
      )}
      <Field label="Country / Basin / Region">
        <TextInput value={payload.country} onChange={(v) => set({ country: v })}
                   placeholder="e.g. USA · Gulf of Mexico" testId="rs-country" />
      </Field>
    </div>
  );
}

/**
 * With exactly ONE pump on the sheet and no circulating duty assigned, that
 * pump is unambiguously the circulating pump — assign it on save so the
 * operator is not asked to restate the only possible answer.
 *
 * Applied at save time (an explicit operator action), never as a background
 * rewrite of stored records. Narrow on purpose:
 *   - only with a single pump, so it can never guess between candidates;
 *   - only when the field is empty, so a real selection is never replaced;
 *   - circulating only. The KILL pump is a well-control decision and is
 *     left for the operator to state, even on a single-pump rig.
 */
function _withSinglePumpDefault(p) {
  const pumps = Array.isArray(p?.pumps) ? p.pumps : [];
  if (pumps.length !== 1) return p;
  const d = p.defaults || {};
  if (d.defaultCirculatingPumpId) return p;
  return { ...p, defaults: { ...d, defaultCirculatingPumpId: pumps[0].id } };
}

function PumpsSection({ payload, set }) {
  const inv = React.useContext(InvalidFieldsContext);
  const units = payload.units || "IMP";
  const updatePump = (idx, patch) => {
    const pumps = payload.pumps.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    set({ pumps });
  };
  const addPump = () => {
    const nextLbl = `Pump ${payload.pumps.length + 1}`;
    set({ pumps: [...payload.pumps, emptyPump(nextLbl)] });
  };
  // Pump duty writes straight to payload.defaults — the SAME field the Rig
  // Defaults dropdowns edit. Two surfaces, one stored value, so the two can
  // never disagree and nothing has to be entered twice. Duty is exclusive,
  // so assigning it to one pump implicitly releases whichever pump held it.
  const DUTIES = [
    { field: "defaultCirculatingPumpId", label: "Primary circulating" },
    { field: "defaultKillPumpId",        label: "Primary kill" },
  ];
  const toggleDuty = (pumpId, field) => {
    const d = payload.defaults || {};
    set({ defaults: { ...d, [field]: d[field] === pumpId ? "" : pumpId } });
  };

  const removePump = (idx) => {
    if (payload.pumps.length <= 1) return;
    const removedId = payload.pumps[idx].id;
    const pumps = payload.pumps.filter((_, i) => i !== idx);
    // Clear any default references to the removed pump.
    const defaults = { ...payload.defaults };
    if (defaults.defaultCirculatingPumpId === removedId) defaults.defaultCirculatingPumpId = "";
    if (defaults.defaultKillPumpId        === removedId) defaults.defaultKillPumpId        = "";
    set({ pumps, defaults });
  };

  return (
    <div className="space-y-3">
      {payload.pumps.map((p, i) => {
        const derived = computePumpDerived(p, units);
        return (
          <div key={p.id} data-testid={`rs-pump-${i}`}
               className="card-rs p-3"
               style={{ borderColor: "rgba(245,180,0,0.35)" }}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="flex items-center gap-2 flex-1">
                <TextInput value={p.label}
                           onChange={(v) => { inv?.clear(`rs-pump-${i}-label`); updatePump(i, { label: v }); }}
                           placeholder={`Pump ${i + 1}`} testId={`rs-pump-${i}-label`}
                           invalid={!!inv?.has(`rs-pump-${i}-label`)} />
              </div>
              <button type="button" data-testid={`rs-pump-${i}-remove`}
                      disabled={payload.pumps.length <= 1}
                      onClick={() => removePump(i)}
                      className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5] disabled:opacity-30"
                      title="Remove pump">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Pump Type">
                <NativeSelect value={p.pumpType} onChange={(v) => updatePump(i, { pumpType: v })}
                              options={PUMP_TYPES_V2.map((t) => ({ value: t, label: PUMP_TYPE_LABELS_V2[t] }))}
                              testId={`rs-pump-${i}-type`} />
              </Field>
              <Field label={`Liner Size (${u.liner(units)})`}>
                <NumIn value={p.linerSize} onChange={(v) => updatePump(i, { linerSize: v })}
                       placeholder={units === "SI" ? "e.g. 152.4" : "e.g. 6"} testId={`rs-pump-${i}-liner`} />
              </Field>
              <Field label={`Stroke Length (${u.stroke(units)})`}>
                <NumIn value={p.strokeLength} onChange={(v) => updatePump(i, { strokeLength: v })}
                       placeholder={units === "SI" ? "e.g. 304.8" : "e.g. 12"} testId={`rs-pump-${i}-stroke`} />
              </Field>
              {p.pumpType === "duplex" && (
                <Field label={`Rod Diameter (${u.dia(units)})`}>
                  <NumIn value={p.rodDiameter} onChange={(v) => updatePump(i, { rodDiameter: v })}
                         placeholder={units === "SI" ? "e.g. 50" : "e.g. 2"} testId={`rs-pump-${i}-rod`} />
                </Field>
              )}
              <Field label="Efficiency (%)">
                <NumIn value={p.efficiency} onChange={(v) => updatePump(i, { efficiency: v })}
                       placeholder="0–100" testId={`rs-pump-${i}-eff`} />
              </Field>
              <Field label="Notes">
                {/* Placeholder deliberately no longer suggests a duty — that
                    used to invite "Primary kill pump" into free text, where
                    nothing could read it. Duty is a real selection below. */}
                <TextInput value={p.notes} onChange={(v) => updatePump(i, { notes: v })}
                           placeholder="e.g. recently rebuilt fluid end" testId={`rs-pump-${i}-notes`} />
              </Field>
            </div>

            <div className="mt-3 flex items-center gap-4 flex-wrap"
                 data-testid={`rs-pump-${i}-duty`}>
              <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--rs-text-3)]">
                Duty
              </span>
              {DUTIES.map(({ field, label }) => (
                <label key={field}
                       className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--rs-text-2)] cursor-pointer">
                  <input type="checkbox"
                         checked={payload.defaults?.[field] === p.id}
                         onChange={() => toggleDuty(p.id, field)}
                         data-testid={`rs-pump-${i}-duty-${field}`} />
                  {label}
                </label>
              ))}
            </div>

            {/* Live computed footer panel */}
            <div data-testid={`rs-pump-${i}-computed`}
                 className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 rounded"
                 style={{ background: "rgba(245,180,0,0.06)", border: "1px dashed rgba(245,180,0,0.4)" }}>
              <ComputedCell label="Output" value={derived?.dispBblPerStk} unit="bbl/stk"
                            testId={`rs-pump-${i}-out-bbl`} />
              <ComputedCell label="Output" value={derived?.dispM3PerStk} unit="m³/stk"
                            testId={`rs-pump-${i}-out-m3`} />
              <ComputedCell label="Strokes" value={derived?.strokesPerBbl} unit="strk/bbl"
                            testId={`rs-pump-${i}-strk-bbl`} />
              <ComputedCell label="Strokes" value={derived?.strokesPerM3} unit="strk/m³"
                            testId={`rs-pump-${i}-strk-m3`} />
            </div>
          </div>
        );
      })}
      <button type="button" data-testid="rs-pump-add"
              onClick={addPump}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium"
              style={{
                background: "transparent", color: "var(--rs-amber)",
                border: "1px dashed var(--rs-amber)", minHeight: 36,
              }}>
        <Plus className="w-4 h-4" /> Add Pump
      </button>
    </div>
  );
}

function ComputedCell({ label, value, unit, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col">
      <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--rs-text-3)]">
        {label}
      </span>
      <span className="text-[13px] font-mono font-semibold text-[var(--rs-text-1)]">
        {value && value !== "0.0000" && value !== "0.000000" ? value : "—"}
      </span>
      <span className="text-[9.5px] text-[var(--rs-text-3)]">{unit}</span>
    </div>
  );
}

function SurfaceLineSection({ payload, set }) {
  const units = payload.units || "IMP";
  const sl = payload.surfaceLine;
  const setSL = (patch) => set({ surfaceLine: { ...sl, ...patch } });
  return (
    <>
      <p className="text-[10.5px] text-[var(--rs-text-3)] leading-snug">
        Surface line geometry. <strong>Volume itself lives in Well Control Volumes</strong>
        (Section 4) — the canonical home for kill-sheet reads.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`Standpipe ID (${u.dia(units)})`}>
          <NumIn value={sl.standpipeId} onChange={(v) => setSL({ standpipeId: v })}
                 placeholder={units === "SI" ? "e.g. 100" : "e.g. 4"} testId="rs-standpipe-id" />
        </Field>
        <Field label={`Standpipe Length (${u.length(units)})`}>
          <NumIn value={sl.standpipeLength} onChange={(v) => setSL({ standpipeLength: v })}
                 placeholder={units === "SI" ? "e.g. 23" : "e.g. 75"} testId="rs-standpipe-len" />
        </Field>
        <Field label={`Rotary Hose ID (${u.dia(units)})`}>
          <NumIn value={sl.rotaryHoseId} onChange={(v) => setSL({ rotaryHoseId: v })}
                 placeholder={units === "SI" ? "e.g. 89" : "e.g. 3.5"} testId="rs-hose-id" />
        </Field>
        <Field label={`Rotary Hose Length (${u.length(units)})`}>
          <NumIn value={sl.rotaryHoseLength} onChange={(v) => setSL({ rotaryHoseLength: v })}
                 placeholder={units === "SI" ? "e.g. 20" : "e.g. 65"} testId="rs-hose-len" />
        </Field>
      </div>
    </>
  );
}

function WellControlVolumesSection({ payload, set, emphasised, stackMode }) {
  const units = payload.units || "IMP";
  const v = payload.wellControlVolumes;
  const setV = (patch) => set({ wellControlVolumes: { ...v, ...patch } });
  const volU = u.vol(units);
  // Master Correction Pass, Defect 7 — Marine Riser, Choke Line, Kill Line
  // and BOP/LMRP volumes are the hydraulic counterparts of the Subsea /
  // Marine System geometry (riser/choke line/kill line IDs) and only apply
  // to a subsea BOP stack. Presentation-only: stored values are preserved
  // when hidden, exactly like the Subsea/Marine System section itself.
  const showSubseaVolumes = stackMode === "subsea";
  return (
    <>
      <p className="text-[10.5px] text-[var(--rs-text-3)] leading-snug">
        Canonical permanent well-control volumes. Future Kill Sheet v2
        worksheets read directly from this section.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`Surface Line Volume (${volU})`}>
          <NumIn value={v.surfaceLineVolume} onChange={(x) => setV({ surfaceLineVolume: x })}
                 placeholder="e.g. 8.5" testId="rs-vol-surfline" />
        </Field>
        {showSubseaVolumes && (
          <>
            <Field label={`Marine Riser Volume (${volU})`}>
              <NumIn value={v.riserVolume} onChange={(x) => setV({ riserVolume: x })}
                     placeholder="e.g. 640" testId="rs-vol-riser" />
            </Field>
            <Field label={`Choke Line Volume (${volU})`}>
              <NumIn value={v.chokeLineVolume} onChange={(x) => setV({ chokeLineVolume: x })}
                     placeholder="e.g. 22" testId="rs-vol-choke" />
            </Field>
            <Field label={`Kill Line Volume (${volU})`}>
              <NumIn value={v.killLineVolume} onChange={(x) => setV({ killLineVolume: x })}
                     placeholder="e.g. 22" testId="rs-vol-kill" />
            </Field>
            <Field label={`BOP / LMRP Volume (${volU})`}>
              <NumIn value={v.bopLmrpVolume} onChange={(x) => setV({ bopLmrpVolume: x })}
                     placeholder="e.g. 18" testId="rs-vol-bop" />
            </Field>
          </>
        )}
      </div>
    </>
  );
}

function SurfaceBopSection({ payload, set, stackMode, location }) {
  const units = payload.units || "IMP";
  const b = payload.surfaceBop;
  const setB = (patch) => set({ surfaceBop: { ...b, ...patch } });
  const wantType = stackMode === "subsea" ? "subsea" : "surface";

  // Subsea Foundation Correction Pass — reuse the existing canonical BOP
  // Reference Library (already protected: seed records read-only, user
  // records fully editable) instead of building a second BOP system.
  // Filtered by the current operating context; an existing selection from
  // before a context flip is preserved and surfaced, never silently
  // dropped, per the same pattern used on the Well Profile BOP picker.
  const bopOptions = useMemo(() => {
    const all = listByCategory("bop").filter((r) => r.bopType === wantType);
    const opts = [
      { value: "", label: "— No BOP record selected —" },
      ...all.map((r) => ({ value: r.id, label: `${r.stackName || r.id}${r.nominalSize ? " · " + r.nominalSize : ""}` })),
    ];
    if (b.bopRefId && !all.some((r) => r.id === b.bopRefId)) {
      const existing = findById(b.bopRefId);
      opts.push({ value: b.bopRefId, label: `${existing ? (existing.stackName || existing.id) : b.bopRefId} (context mismatch)` });
    }
    return opts;
  }, [wantType, b.bopRefId]);
  const selectedBop = b.bopRefId ? findById(b.bopRefId) : null;
  const bopMismatch = !!b.bopRefId && (!selectedBop || selectedBop.bopType !== wantType);

  const onPickBop = (v) => {
    const rec = v ? findById(v) : null;
    // Narrow explicit adapter (item 10) — only fields with an exact,
    // unambiguous semantic + unit match on THIS rig sheet are copied.
    // This is a one-time snapshot, not a live link: editing/deleting the
    // library record later never changes an already-saved Rig Data Sheet.
    // Nothing is invented into the mismatched annular/pipeRams/blindRams/
    // shearRams free-text fields — those remain manual.
    if (rec && wantType === "subsea") {
      set({
        surfaceBop: { ...b, bopRefId: v },
        subsea: { ...payload.subsea, chokeLineId: rec.chokeLineId || payload.subsea.chokeLineId,
                                       killLineId:  rec.killLineId  || payload.subsea.killLineId },
      });
    } else {
      setB({ bopRefId: v });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <Field label={`BOP from Reference Library (${wantType === "subsea" ? "Subsea" : "Surface"} records)`}
               helper="One-time snapshot copy of choke/kill line ID only — no live link to the library.">
          <NativeSelect value={b.bopRefId || ""} onChange={onPickBop}
                        options={bopOptions} testId="rs-bop-refId" />
        </Field>
        <RouterLink to="/reference-libraries/bop"
           state={libraryReturnState(location, payload, "rs-section-surfbop")}
           data-testid="rs-bop-manage"
           className="inline-flex items-center gap-1 text-[10.5px] text-amber-400 mb-2">
          <Library className="w-3 h-3" /> Manage BOP Library
        </RouterLink>
      </div>
      {bopMismatch && (
        <p className="text-[11px] text-amber-400 mb-2" data-testid="rs-bop-context-mismatch">
          The selected BOP record doesn't match the current operating context (Surface/Subsea) — review before proceeding.
        </p>
      )}
      {selectedBop && (
        <div className="card-rs p-2.5 mb-3 text-[11px] leading-snug" data-testid="rs-bop-reference-card"
             style={{ background: "rgba(245,180,0,0.04)", borderColor: "rgba(245,180,0,0.25)" }}>
          <div className="font-mono uppercase tracking-wider text-[9.5px] text-[var(--rs-amber)] mb-1">
            Library reference — {selectedBop.stackName || selectedBop.id}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-[var(--rs-text-2)]">
            <div>Type: {selectedBop.bopType}</div>
            <div>Nominal size: {selectedBop.nominalSize || "—"}</div>
            <div>Working pressure: {selectedBop.workingPressure || "—"} psi</div>
            <div>Ram slots: {selectedBop.ramSlots || "—"}</div>
            <div>Annular count: {selectedBop.annularCount || "—"}</div>
            <div>Choke line ID: {selectedBop.chokeLineId || "—"}</div>
            <div>Kill line ID: {selectedBop.killLineId || "—"}</div>
            <div>Accumulator: {selectedBop.accumulatorGal || "—"} gal</div>
            <div>Precharge: {selectedBop.accumulatorPrechargePsi || "—"} psi</div>
          </div>
        </div>
      )}
      <Checkbox checked={!!b.present}
                onChange={(v) => setB({ present: v })}
                label={wantType === "subsea" ? "Subsea BOP / LMRP present" : "Surface BOP present"}
                testId="rs-bop-present" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Annular (label / spec)">
          <TextInput value={b.annular} onChange={(v) => setB({ annular: v })}
                     placeholder="e.g. Hydril GX 18-3/4 in" testId="rs-bop-annular" />
        </Field>
        <Field label="Pipe Rams">
          <TextInput value={b.pipeRams} onChange={(v) => setB({ pipeRams: v })}
                     placeholder="e.g. 2 × 5-1/2 VBR" testId="rs-bop-pipe" />
        </Field>
        <Field label="Blind Rams">
          <TextInput value={b.blindRams} onChange={(v) => setB({ blindRams: v })}
                     placeholder="e.g. 1 × Blind/Shear" testId="rs-bop-blind" />
        </Field>
        <Field label="Shear Rams">
          <TextInput value={b.shearRams} onChange={(v) => setB({ shearRams: v })}
                     placeholder="e.g. 1 × CSO" testId="rs-bop-shear" />
        </Field>
        <Field label={`Annular Working Pressure (${u.press(units)})`}>
          <NumIn value={b.annularWorkingPressure} onChange={(v) => setB({ annularWorkingPressure: v })}
                 placeholder="e.g. 5000" testId="rs-bop-anwp" />
        </Field>
        <Field label={`Ram Working Pressure (${u.press(units)})`}>
          <NumIn value={b.ramWorkingPressure} onChange={(v) => setB({ ramWorkingPressure: v })}
                 placeholder="e.g. 15000" testId="rs-bop-ramwp" />
        </Field>
      </div>
    </>
  );
}

function SubseaSection({ payload, set, emphasised }) {
  const units = payload.units || "IMP";
  const s = payload.subsea;
  const setS = (patch) => set({ subsea: { ...s, ...patch } });
  return (
    <>
      <p className="text-[10.5px] text-[var(--rs-text-3)] leading-snug">
        Subsea geometry (water depth + line IDs). Volumes are stored in
        Section 4 · Well Control Volumes.
        {!emphasised && " · Optional for this rig type."}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`Water Depth (${u.depth(units)})`}>
          <NumIn value={s.waterDepth} onChange={(v) => setS({ waterDepth: v })}
                 placeholder="e.g. 5000" testId="rs-sub-wd" />
        </Field>
        <Field label={`Marine Riser ID (${u.dia(units)})`}>
          <NumIn value={s.riserId} onChange={(v) => setS({ riserId: v })}
                 placeholder="e.g. 21" testId="rs-sub-riser-id" />
        </Field>
        <Field label={`Choke Line ID (${u.dia(units)})`}>
          <NumIn value={s.chokeLineId} onChange={(v) => setS({ chokeLineId: v })}
                 placeholder="e.g. 3.5" testId="rs-sub-choke-id" />
        </Field>
        <Field label={`Kill Line ID (${u.dia(units)})`}>
          <NumIn value={s.killLineId} onChange={(v) => setS({ killLineId: v })}
                 placeholder="e.g. 3.5" testId="rs-sub-kill-id" />
        </Field>
      </div>
    </>
  );
}

function ChokeManifoldSection({ payload, set }) {
  const c = payload.chokeManifold;
  const setC = (patch) => set({ chokeManifold: { ...c, ...patch } });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Manifold Label / Description">
        <TextInput value={c.label} onChange={(v) => setC({ label: v })}
                   placeholder="e.g. Cameron Type-J" testId="rs-choke-label" />
      </Field>
      <Field label="Choke Type">
        <TextInput value={c.type} onChange={(v) => setC({ type: v })}
                   placeholder="e.g. Adjustable hydraulic" testId="rs-choke-type" />
      </Field>
      <Field label="Number of Chokes">
        <NumIn value={c.numberOfChokes} onChange={(v) => setC({ numberOfChokes: v })}
               placeholder="e.g. 2" testId="rs-choke-count" />
      </Field>
      <Field label="Notes">
        <TextInput value={c.notes} onChange={(v) => setC({ notes: v })}
                   placeholder="Optional" testId="rs-choke-notes" />
      </Field>
    </div>
  );
}

function DefaultsSection({ payload, set }) {
  const d = payload.defaults;
  const setD = (patch) => set({ defaults: { ...d, ...patch } });
  const pumpOpts = [
    { value: "", label: "— none —" },
    ...payload.pumps.map((p) => ({ value: p.id, label: p.label || "Pump" })),
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Default Circulating Pump"
               helper="Pump used for routine circulation.">
          <NativeSelect value={d.defaultCirculatingPumpId}
                        onChange={(v) => setD({ defaultCirculatingPumpId: v })}
                        options={pumpOpts}
                        testId="rs-default-circ" />
        </Field>
        <Field label="Default Kill Pump"
               helper="Pump preferred for well-control / kill operations. May be the same as the circulating pump.">
          <NativeSelect value={d.defaultKillPumpId}
                        onChange={(v) => setD({ defaultKillPumpId: v })}
                        options={pumpOpts}
                        testId="rs-default-kill" />
        </Field>
      </div>
      <Field label="Rig Notes" helper="Free-text rig-level notes, caveats, or remarks.">
        <TextArea value={d.rigNotes} onChange={(v) => setD({ rigNotes: v })}
                  rows={3} placeholder="e.g. Maximum surface pressure during well control: …"
                  testId="rs-rig-notes" />
      </Field>
    </div>
  );
}

// =============================================================================
// Failed-save focus support
//
// `validateRigSheet` (lib/rigsheet/schema.js) returns prose strings with no
// field identifiers or paths, so the first offending field is resolved by
// matching those strings to the `data-testid` each input already renders.
// This is lookup data only — no shared input component is touched. Any
// message without a mapping falls back to the error panel, which already
// lists every failure verbatim.
// =============================================================================
const PUMP_FIELD_BY_ERROR = [
  [/Pump Type/,       "type"],
  [/Liner Size/,      "liner"],
  [/Stroke Length/,   "stroke"],
  [/Rod Diameter/,    "rod"],
  [/Efficiency/,      "eff"],
  [/duplicate label/, "label"],
];

// Order matters where one label is a prefix of another (Volume before ID).
const FIELD_BY_ERROR = [
  [/^Rig Name/,                 "rs-rig-name"],
  [/^Rig Type/,                 "rs-rig-type"],
  [/^Default Unit System/,      "rs-units-imp"],
  [/^At least one mud pump/,    "rs-pump-add"],
  [/^Standpipe ID/,             "rs-standpipe-id"],
  [/^Standpipe Length/,         "rs-standpipe-len"],
  [/^Rotary Hose ID/,           "rs-hose-id"],
  [/^Rotary Hose Length/,       "rs-hose-len"],
  [/^Surface Line Volume/,      "rs-vol-surfline"],
  [/^Marine Riser Volume/,      "rs-vol-riser"],
  [/^Choke Line Volume/,        "rs-vol-choke"],
  [/^Kill Line Volume/,         "rs-vol-kill"],
  [/^BOP \/ LMRP Volume/,       "rs-vol-bop"],
  [/^Annular Working Pressure/, "rs-bop-anwp"],
  [/^Ram Working Pressure/,     "rs-bop-ramwp"],
  [/^Water Depth/,              "rs-sub-wd"],
  [/^Marine Riser ID/,          "rs-sub-riser-id"],
  [/^Choke Line ID/,            "rs-sub-choke-id"],
  [/^Kill Line ID/,             "rs-sub-kill-id"],
  [/^Number of chokes/,         "rs-choke-count"],
  [/^Default Circulating Pump/, "rs-default-circ"],
  [/^Default Kill Pump/,        "rs-default-kill"],
];

function testIdForError(msg) {
  const pump = /^Pump #(\d+):\s*(.*)$/.exec(msg);
  if (pump) {
    const idx = Number(pump[1]) - 1;
    const hit = PUMP_FIELD_BY_ERROR.find(([re]) => re.test(pump[2]));
    return hit ? `rs-pump-${idx}-${hit[1]}` : `rs-pump-${idx}`;
  }
  const hit = FIELD_BY_ERROR.find(([re]) => re.test(msg));
  return hit ? hit[1] : null;
}

// Takes the operator to the first field that blocked the save and leaves the
// browser's own focus ring on it. Falls back to the error panel (which names
// every failure) when no message maps to a field.
function focusFirstInvalid(errors) {
  const el =
    errors
      .map((m) => testIdForError(m))
      .filter(Boolean)
      .map((id) => document.querySelector(`[data-testid="${id}"]`))
      .find(Boolean) || document.querySelector('[data-testid="rs-errors"]');
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  if (typeof el.focus === "function") el.focus({ preventScroll: true });
}

// Every field the current validation errors blame, not just the first. Marking
// is driven exclusively by this set, which is only ever populated from a failed
// validateRigSheet() result — so no field can render as invalid without a
// failed save having produced an error for it.
function invalidTestIds(errors) {
  return new Set(errors.map((m) => testIdForError(m)).filter(Boolean));
}

const NO_INVALID_FIELDS = new Set();

// Deferred one frame so the error panel exists in the DOM before it can be
// used as the fallback target (on the first failure React has not yet
// rendered it when handleSave runs). Field targets are always mounted, so
// the common path is unaffected either way.
function focusFirstInvalidDeferred(errors) {
  requestAnimationFrame(() => focusFirstInvalid(errors));
}

// =============================================================================
// Page component
// =============================================================================
export default function RigSheetEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;                       // arrived on /rig-sheets/:id — route-scoped only
  // Read before the payload initializer below so a brand-new sheet defaults
  // to a rig type compatible with the current global operating mode
  // (previously always defaulted to "Land" even under Subsea — item 4).
  const { stackMode } = useStackMode();

  const [payload, setPayload] = useState(() => {
    const base = emptyRigSheet();
    if (stackMode === "subsea") base.rigType = "Drillship";
    return base;
  });
  const [loadedRow, setLoadedRow] = useState(null);
  const [errors, setErrors] = useState([]);
  const [notFound, setNotFound] = useState(false);
  // Persistent local save state, rendered in the bottom action bar so the
  // outcome is visible where the operator is actually working rather than
  // only in the 4s toast. Cleared by the next edit (see `set` below) so a
  // stale "Changes saved" can never sit above unsaved work.
  const [saveNotice, setSaveNotice] = useState(null); // "created" | "saved" | "failed" | null
  // testIds of the fields blamed by the most recent failed save. Only ever
  // written from a failed validateRigSheet() result (see handleSave).
  const [invalidFields, setInvalidFields] = useState(NO_INVALID_FIELDS);
  // Id of a record created during THIS mount. The create path deliberately no
  // longer navigates (see handleSave), so the route param stays undefined and
  // can no longer be what tells a save "this record already exists". Without
  // this, a second Save after a create would create a second record.
  const [createdId, setCreatedId] = useState(null);
  // The record this editor is bound to, whether it came from the route or was
  // created a moment ago. Every save decision routes on this, never on isEdit.
  // Declared after the state above — reading `createdId` earlier would hit its
  // temporal dead zone and throw on the first render.
  const effectiveId = id || createdId;
  const isExisting = !!effectiveId;

  useEffect(() => {
    if (!isEdit) return;
    const row = getRigSheet(id);
    if (!row) { setNotFound(true); return; }
    setLoadedRow(row);
    setPayload(row.payload);
    setInvalidFields(NO_INVALID_FIELDS);
  }, [id, isEdit]);

  // Reference Library Return-Workflow — restore in-progress edits that
  // survived a round trip to the BOP Reference Library. Consumed once,
  // then cleared from history state so a later refresh never re-applies it.
  useEffect(() => {
    const draft = getRestoredDraft(location);
    if (!draft) return;
    const matches = isEdit ? draft.id === id : !draft.id;
    if (matches) setPayload(draft);
    scrollToRestoredAnchor(getRestoredAnchor(location));
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => {
    // Any operator edit invalidates the previous save outcome.
    setSaveNotice(null);
    setPayload((cur) => ({ ...cur, ...patch }));
  };
  // A no-op clear returns the previous Set unchanged so React can bail out of
  // the re-render when the field carried no marker.
  const invalidCtx = useMemo(
    () => ({
      has: (id) => invalidFields.has(id),
      clear: (id) =>
        setInvalidFields((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        }),
    }),
    [invalidFields]
  );
  const softNotices = useMemo(() => rigSheetSoftNotices(payload), [payload]);
  const subseaEmphasised = SUBSEA_EMPHASISED_TYPES.has(payload.rigType);
  // Correction 1 — Subsea / Marine System visibility follows the global
  // Surface/Subsea operating mode, not rig type. Presentation-only: the
  // `payload.subsea` data itself is never cleared, so switching the global
  // mode back and forth never destroys previously saved subsea values.

  const handleSave = (advanceTo) => {
    const baked = _withSinglePumpDefault(recomputeAllPumps(payload));
    const result = validateRigSheet(baked);
    if (!result.ok) {
      // A failed save must never resemble a successful one or a navigation:
      // it states plainly that nothing was saved, keeps that statement in the
      // action bar, and moves the operator to the first blocking field. The
      // error panel continues to name every failure.
      setErrors(result.errors);
      setInvalidFields(invalidTestIds(result.errors));
      setSaveNotice("failed");
      toast.error("Rig Data Sheet not saved");
      focusFirstInvalidDeferred(result.errors);
      return;
    }
    try {
      const wasNew = !effectiveId;
      const row = effectiveId
        ? updateRigSheet(effectiveId, baked)
        : createRigSheet(baked);
      // Root-cause repair (Master Correction Pass, Defect 1/2) — auto-activate
      // this rig in the Engineering Session on first create, mirroring the
      // existing "auto-link well to rig" pattern in WellProfileEditor. Never
      // overwrites an already-active rig binding.
      if (wasNew) {
        const s = getEngineeringSession();
        if (!s || !s.rigId) updateEngineeringSession({ rigId: row.id });
      }
      // Bind this mount to the new record BEFORE anything else can re-enter
      // handleSave, so every subsequent save updates it instead of creating.
      if (wasNew) setCreatedId(row.id);
      toast.success(wasNew ? "Rig Data Sheet created" : "Rig Data Sheet saved");
      setSaveNotice(wasNew ? "created" : "saved");
      setInvalidFields(NO_INVALID_FIELDS);
      if (typeof advanceTo === "string" && advanceTo) {
        navigate(advanceTo);
        return;
      }
      // No navigation on save — not on create, not on edit.
      //
      // The create path used to replace /rig-sheets/new with /rig-sheets/:id
      // purely to make `isEdit` true, and that pathname change fired the
      // global ScrollToTop. The double-rAF + macrotask restoration that tried
      // to undo it was a race against React's passive-effect flush with no
      // guaranteed winner — it held at desktop width and lost at phone width.
      // `createdId` now carries the "already exists" fact instead, so the
      // pathname never changes, ScrollToTop cannot fire, and the operator's
      // scroll position is simply never disturbed. Nothing to restore.
      //
      // Consequence, accepted deliberately: the URL stays /rig-sheets/new
      // until the operator leaves the page, so a mid-edit browser refresh
      // loses the deep link and reopens an empty new sheet — the record
      // itself is already persisted and is listed in the Rig Data Sheets hub.
      // Not worked around with history.replaceState, which would desync
      // React Router's own location from the address bar.

      // Replace local state with the freshly-saved row so the version pill updates.
      setLoadedRow(row);
      setPayload(row.payload);
      setErrors([]);
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Save failed"]);
    }
  };

  if (notFound) {
    return (
      <div className="p-4 max-w-2xl mx-auto" data-testid="rs-not-found">
        <h1 className="font-heading text-2xl mb-2">Rig Data Sheet not found</h1>
        <button type="button" onClick={() => navigate("/rig-sheets")}
                className="text-[var(--rs-amber)]">← Back to Rig Data Sheets</button>
      </div>
    );
  }

  return (
    <InvalidFieldsContext.Provider value={invalidCtx}>
    <div data-testid="rig-sheet-editor" className="flex flex-col gap-4 pb-12">
      {/* Header */}
      <header className="flex items-center justify-between gap-3">
        <button type="button" data-testid="rs-back"
                onClick={() => navigate("/rig-sheets")}
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Rig Data Sheets
        </button>
        {loadedRow && (
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)]"
               data-testid="rs-version-pill">
            v{loadedRow.version} · updated {new Date(loadedRow.updatedAt).toLocaleString()}
          </div>
        )}
      </header>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--rs-amber)]">
          Kill Sheet v2 · Foundation
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight mt-1">
          {isExisting ? "Edit Rig Data Sheet" : "New Rig Data Sheet"}
        </h1>
        <p className="text-[12px] text-[var(--rs-text-2)] mt-1 leading-snug">
          Rig-level permanent data — pump output, surface line volume, riser/choke/kill volumes,
          BOP equipment. Future Kill Sheets read from this record automatically.
        </p>
      </div>

      <RceEditorStrip
        ownerKey="rigSheet"
        ownerLabel="Rig Data Sheet · Engineering Context"
        consumes={["project.operator", "project.client", "project.country", "project.field"]}
      />

      {/* Validation errors */}
      {errors.length > 0 && (
        <div data-testid="rs-errors"
             className="card-rs p-3"
             style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#FCA5A5" }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#FCA5A5" }}>
              Cannot save — please fix:
            </span>
          </div>
          <ul className="list-disc list-inside text-[11.5px] text-[var(--rs-text-1)]">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Soft notices */}
      {softNotices.length > 0 && (
        <div data-testid="rs-soft-notices"
             className="card-rs p-2"
             style={{ borderColor: "rgba(245,180,0,0.4)", background: "rgba(245,180,0,0.04)" }}>
          {softNotices.map((n, i) => (
            <div key={i} className="text-[11px] text-[var(--rs-text-2)] leading-snug">
              <span className="text-[var(--rs-amber)] font-mono uppercase text-[9.5px] tracking-[0.18em] mr-1">
                Notice ·
              </span>
              {n}
            </div>
          ))}
        </div>
      )}

      {/* Sections */}
      <SectionCard id="identity" title="1 · Rig Identity" Icon={Building2}>
        <IdentitySection payload={payload} set={set} stackMode={stackMode} />
      </SectionCard>

      <SectionCard id="pumps" title="2 · Mud Pumps" Icon={Gauge}>
        <PumpsSection payload={payload} set={set} />
      </SectionCard>

      <SectionCard id="surfline" title="3 · Surface Line Data" Icon={ListChecks}>
        <SurfaceLineSection payload={payload} set={set} />
      </SectionCard>

      <SectionCard id="volumes" title="4 · Well Control Volumes" Icon={Waves}
                   emphasised>
        <WellControlVolumesSection payload={payload} set={set} emphasised stackMode={stackMode} />
      </SectionCard>

      <SectionCard id="surfbop"
                   title={stackMode === "subsea" ? "5 · Subsea BOP / LMRP Stack" : "5 · Surface BOP Stack"}
                   Icon={ShieldAlert}>
        <SurfaceBopSection payload={payload} set={set} stackMode={stackMode} location={location} />
      </SectionCard>

      {stackMode === "subsea" && (
        <SectionCard id="subsea" title="6 · Subsea / Marine System" Icon={Anchor}
                     emphasised={subseaEmphasised}>
          <SubseaSection payload={payload} set={set} emphasised={subseaEmphasised} />
        </SectionCard>
      )}

      <SectionCard id="choke" title="7 · Choke Manifold" Icon={ListChecks}>
        <ChokeManifoldSection payload={payload} set={set} />
      </SectionCard>

      <SectionCard id="defaults" title="8 · Rig Defaults & Notes" Icon={ListChecks}>
        <DefaultsSection payload={payload} set={set} />
      </SectionCard>

      {/* Save bar — inline above the bottom nav. Adequate padding-bottom on
          parent ensures it never sits underneath the bottom navigation. */}
      <div id="rs-save-bar"
           className="mt-2 flex flex-wrap items-center justify-end gap-2 card-rs px-3 py-2"
           style={{
             background: "rgba(10,11,14,0.92)",
             // The bottom nav is fixed at z-index 10000 and is
             // 58px content + 1px border + pb-safe (env inset + 8px) tall,
             // so a plain scrollIntoView({block:"end"}) parks this bar
             // underneath it. scroll-margin-bottom is what `block:"end"`
             // aligns against, so reserving the nav's height plus the same
             // env() inset it uses — plus 16px of clearance — lands the whole
             // bar above the nav on every device, including phones with a
             // home-bar inset that a fixed pixel offset would miss.
             scrollMarginBottom: "calc(83px + env(safe-area-inset-bottom, 0px))",
           }}>
        {saveNotice && (
          <span data-testid="rs-save-notice"
                role="status"
                className="w-full sm:w-auto sm:mr-auto inline-flex items-center gap-1.5 text-[12px] font-medium"
                style={{ color: saveNotice === "failed" ? "#FCA5A5" : "var(--rs-amber)" }}>
            {saveNotice === "failed"
              ? <AlertTriangle className="w-3.5 h-3.5" />
              : <CheckCircle2 className="w-3.5 h-3.5" />}
            {saveNotice === "created"
              ? "Rig Data Sheet created"
              : saveNotice === "saved"
              ? "Changes saved"
              : "Rig Data Sheet not saved"}
          </span>
        )}
        <button type="button" data-testid="rs-cancel"
                onClick={() => navigate("/rig-sheets")}
                className="px-4 py-2 rounded-md text-[13px]"
                style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
          Cancel
        </button>
        <button type="button" data-testid="rs-save"
                onClick={() => handleSave()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
          <Save className="w-4 h-4" /> {isExisting ? "Save changes" : "Create Rig Data Sheet"}
        </button>
        <button type="button" data-testid="rs-save-next"
                onClick={() => handleSave("/wells")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                style={{ background: "transparent", color: "var(--rs-amber)", border: "1px solid var(--rs-amber)", minHeight: 40 }}>
          Next → Well Profiles
        </button>
      </div>

      {/* RC-1 — Floating Engineering Toolbar */}
      <FloatingActionToolbar
        testId="rs-fab"
        actions={[
          { id: "save", icon: Save, label: "Save", onClick: () => handleSave(), testId: "fab-rs-save", primary: true },
        ]}
      />
    </div>
    </InvalidFieldsContext.Provider>
  );
}
